import React, { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useAppSelector } from '../../data/store'
import { creditApplicationEffects, creditApplicationSelectors } from '../../data/store/CreditApplicationStore'
import { useSideEffect } from '../../data/store/Effects/side-effects'
import { ComputeAmountForFrequencyDto } from '../../data/types/ComputeAmountForFrequencyDto'
import { Constants, Enum } from '../../data/types/constants'
import {
  FormatCurrency,
  FormatCurrencyNoDecimals,
  FormatCurrencyToNearestUpperMultipleToNoDecimals,
} from '../utils/Formatter'
import { trackingEvents } from '../utils/trackingEvents'
import LoanTableComponent from './LoanTableComponent'

type Props = {
  onSubmit: (event: { preventDefault: () => void }) => void
  cancelMyApp: (event: { preventDefault: () => void }) => void
  gotoAddCoapplicant: () => void
  stepProgression: React.ReactElement
}

function FinalDecisionApprovedForm({ onSubmit, cancelMyApp, gotoAddCoapplicant, stepProgression }: Props) {
  const { t } = useTranslation()
  const dispatchEffect = useSideEffect()

  const creditApplication = useAppSelector(creditApplicationSelectors.getFilteredCreditApplication)
  const computedInfo = useAppSelector(creditApplicationSelectors.getComputedInfo)
  const computedAmounts = useAppSelector(creditApplicationSelectors.getComputedAmounts)

  const [minPayment, setMinPayment] = useState<number | undefined>()

  const [isSubmitted, setSubmitted] = React.useState<boolean>(false)
  function innerSubmit(event: { preventDefault: () => void }) {
    setSubmitted(true)
    onSubmit(event)
  }

  const hasPromotion = creditApplication?.merchantPaymentPlanId

  React.useEffect(() => {
    if (hasPromotion) {
      dispatchEffect(creditApplicationEffects.computePersonalLoanFunding({ id: creditApplication?.id as string }))
    }

    if (
      creditApplication?.finalDecision.maxAmountFinanced !== null &&
      creditApplication?.finalDecision.interestRate !== null
    ) {
      const dto: ComputeAmountForFrequencyDto = {
        amount: creditApplication?.finalDecision.maxAmountFinanced,
        adminFee: Constants.FinanceFeeRateForRegular * (creditApplication?.finalDecision.maxAmountFinanced ?? 0),
        rate: creditApplication?.finalDecision.interestRate,
        paymentFrequency: Enum.EPaymentFrequencyEnum.Monthly,
        activationDate: new Date(),
      }

      dispatchEffect(creditApplicationEffects.computeAmountForFrequency(dto))
    }
  }, [])

  React.useEffect(() => {
    if (computedAmounts !== null)
      setMinPayment(computedAmounts?.termAmountPair[computedAmounts.termAmountPair.length - 1]?.amount)
  }, [computedAmounts])

  React.useEffect(() => {
    if (creditApplication) {
      trackingEvents.qualifSuccess({
        credit_application_id: creditApplication.id,
        reference_number: creditApplication.referenceNumber,
        amount: creditApplication.finalDecision.maxAmountFinanced,
      })
    }
  }, [creditApplication])

  return (
    <main className="general-message" style={{ paddingTop: '15rem' }}>
      {stepProgression}
      <form onSubmit={innerSubmit}>
        <div>
          <h1>{t('decision.hardhitDecisionApprovedTitle')}</h1>
          <div className="paragraph">
            {hasPromotion && computedInfo?.paymentForFrequency && (
              <Trans
                i18nKey="decision.hardhitDecisionApprovedVariableInterest"
                values={{
                  maximumAmount: FormatCurrencyNoDecimals(creditApplication?.finalDecision.maxAmountFinanced).replace(
                    ' ',
                    '\u202F',
                  ),
                  reducedInterestRate: creditApplication?.merchantPaymentPlan.reducedInterestRate,
                  reducedRateDurationInMonths: creditApplication?.merchantPaymentPlan.reducedRateDurationInMonths,
                  interestRate: creditApplication?.merchantPaymentPlan.interestRate,
                  monthlyPayment: FormatCurrencyToNearestUpperMultipleToNoDecimals(
                    computedInfo.paymentForFrequency,
                    5,
                  ).replace(' ', '\u202F'),
                }}
              />
            )}

            {!hasPromotion && (
              <p>
                <Trans
                  i18nKey="decision.hardhitDecisionApproved"
                  values={{
                    maximumAmount: FormatCurrencyNoDecimals(creditApplication?.finalDecision.maxAmountFinanced),
                    minPaymentAmount: FormatCurrency(minPayment),
                  }}
                />
              </p>
            )}

            {creditApplication && (
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 15 }}>
                {!hasPromotion && <LoanTableComponent />}
              </div>
            )}
            <span style={{ fontSize: '1.5rem' }}>
              <sup style={{ color: 'red' }}>*</sup>
              {t<string>('common.monthlyAmountsInclude', {
                financeFee: hasPromotion ? creditApplication.merchantPaymentPlan.borrowerFeeRate : 6,
                interestRate: creditApplication?.finalDecision.interestRate,
              })}
            </span>
          </div>
          <div className="button-group-3">
            <div className="item">
              <button type="button" className="btn" onClick={cancelMyApp}>
                <span>{t<string>('common.cancelMyApp')}</span>
                <i className="fa-regular fa-ban" />
              </button>
            </div>
            <div className="item">
              <button
                type="button"
                className="btn"
                onClick={gotoAddCoapplicant}
                disabled={creditApplication?.hasCoapplicant}
              >
                <span>{t<string>('common.addCoapplicant')}</span>
                <i className="fa-regular fa-user-plus" />
              </button>
            </div>
            <div className="item">
              <button type="submit" className="btn btn-blue" disabled={isSubmitted}>
                <span>{t('finalDecision.continue')}</span>
                <i className="fa-regular fa-arrow-right" />
              </button>
            </div>
          </div>
        </div>
      </form>
    </main>
  )
}

export default FinalDecisionApprovedForm
